import { useState, useRef, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { TiptapEditor, TiptapEditorRef } from "@/components/TiptapEditor";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronUp, ChevronDown, Send } from "lucide-react";
import { createSubmission } from "@/api/submissionApi";
import { toast } from "sonner";

interface SubmissionEditorProps {
  onSubmit: (content: string) => void;
  content?: string;
  setContent?: (content: string) => void;
  questionId: string;
}

export interface SubmissionEditorRef {
  addContent: (content: string) => void;
}

export const SubmissionEditor = forwardRef<
  SubmissionEditorRef,
  SubmissionEditorProps
>(
  (
    { onSubmit, content: propContent, setContent: propSetContent, questionId },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(true);
    const [localContent, setLocalContent] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const editorRef = useRef<TiptapEditorRef>(null);

    // Use either the props or local state for content management
    const content = propContent !== undefined ? propContent : localContent;
    const setContent = propSetContent || setLocalContent;

    // Expose the addContent method to parent components
    useImperativeHandle(
      ref,
      () => ({
        addContent: (newContent: string) => {
          // Open the editor if it's closed
          if (!isOpen) {
            setIsOpen(true);
          }

          // Add content directly to the editor using the ref
          if (editorRef.current) {
            editorRef.current.addContent(newContent);
          }
        },
      }),
      [isOpen]
    );

    const handleSubmit = async () => {
      if (content.trim()) {
        try {
          setIsSubmitting(true);
          // Create submission DTO
          const submissionData = {
            question_id: questionId,
            submission: content.trim(),
          };
          console.log("submissionData", submissionData);
          // Send to backend
          await createSubmission(submissionData);

          // Call the onSubmit callback
          onSubmit(content);

          // Clear the editor
          setContent("");

          toast.success("Submission sent successfully!");
        } catch (error) {
          console.error("Error submitting content:", error);
          toast.error("Failed to submit. Please try again.");
        } finally {
          setIsSubmitting(false);
        }
      }
    };

    return (
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="w-full border border-white/10 rounded-lg mt-3"
      >
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full flex items-center justify-between p-2 hover:bg-card/30 text-sm"
          >
            <span className="font-medium">
              {isOpen ? "Your Submission" : "Expand Submission Editor"}
            </span>
            {isOpen ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="p-3">
          <TiptapEditor
            ref={editorRef}
            content={content}
            onChange={setContent}
            className="min-h-[150px]"
          />

          <div className="mt-3 flex justify-end">
            <Button
              onClick={handleSubmit}
              className="bg-gradient-to-r from-primary to-secondary text-white text-sm py-1"
              disabled={!content.trim() || isSubmitting}
            >
              <Send className="mr-1 h-3 w-3" />
              {isSubmitting ? "Submitting..." : "Submit Writing"}
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }
);

SubmissionEditor.displayName = "SubmissionEditor";
